import { useState, useRef, useCallback, useEffect, useId, useMemo, memo } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Activity,
  Loader2,
  ImageOff,
  Calendar,
  Camera,
  FolderOpen,
  X,
  ChevronDown,
  SlidersHorizontal,
  Image,
  Video,
  Play,
  Search,
  Trash2,
  MoreVertical,
  CheckSquare,
  Download,
  Star,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { DateRangePicker } from '@/components/ui/date-range-picker'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { eventsApi, camerasApi, aiModelsApi } from '@/api/endpoints'
import { queryKeys, queryOptions } from '@/config/queryConfig'
import { formatCardDateTime, cn } from '@/lib/utils'
import { getEventDownloads, downloadFile } from '@/lib/eventDownload'
import { eventsSchema, EVENTS_SCHEMA_NO_CATEGORY, type EventsSchema } from '@/lib/urlSchemas'
import type { StateOf } from '@/lib/urlState'
import { withSearchParam, type LocationStateWithFrom } from '@/lib/navigation'
import type { Event, Camera as CameraType, VisualDetection, DefaultEventCategory } from '@/types'
import { EventDetailModal } from '@/components/events/EventDetailModal'
import { UnreadBadge } from '@/components/events/UnreadBadge'
import { CameraTelemetryPills } from '@/components/cameras/CameraTelemetryPills'
import { useToggleFavorite } from '@/hooks/useToggleFavorite'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { useEventTitle } from '@/hooks/useEventTitle'  // #294
import { useUrlState } from '@/hooks/useUrlState'
import { useRestorableList } from '@/hooks/useRestorableList'
import { useImageRetry } from '@/hooks/useImageRetry'  // #336
import { useAcknowledgeCritical } from '@/hooks/useAcknowledgeCritical'  // #392
import { criticalReasonText, isCriticalEvent, type Translate } from '@/lib/criticalEvents'  // #392
import { useClueReasons, useMarkSeen } from '@/hooks/useSuspiciousEvents'  // #406
import { useSuspiciousBeta } from '@/hooks/useSuspiciousBeta'  // #408
import { isOpenSuspicious, isSuspiciousEvent } from '@/lib/suspiciousEvents'  // #406
import { SuspiciousSeenLine } from './SuspiciousEventRow'  // #406

// Media type badge shown on the thumbnail
function MediaTypeBadge({ mediaType }: { mediaType: 'video' | 'image' }) {
  const { t } = useTranslation('events')
  return (
    <div className="absolute top-2 left-2 flex items-center gap-1 rounded-full bg-black/70 px-2 py-0.5 text-[10px] font-medium text-white">
      {mediaType === 'video' ? (
        <>
          <Video className="h-3 w-3" />
          {t('types.video')}
        </>
      ) : (
        <>
          <Image className="h-3 w-3" />
          {t('types.photo')}
        </>
      )}
    </div>
  )
}

// Event media component — shows video player if available, otherwise thumbnail image
function EventMedia({
  thumbnailUrl,
  videoUrl,
  mediaType,
  alt,
  showVideo,
  setShowVideo,
  onImageError,
}: {
  thumbnailUrl: string | null
  videoUrl: string | null
  mediaType: 'video' | 'image'
  alt: string
  showVideo: boolean
  setShowVideo: (v: boolean) => void
  /** #336 — the thumbnail failed (an expired link): the feed refreshes its list once, so a fresh link arrives */
  onImageError?: () => void
}) {
  const { t } = useTranslation('events')
  const [imageStatus, setImageStatus] = useState<'loading' | 'loaded' | 'error'>(
    thumbnailUrl ? 'loading' : 'error'
  )

  useEffect(() => {
    setImageStatus(thumbnailUrl ? 'loading' : 'error')
  }, [thumbnailUrl])

  const isVideo = mediaType === 'video'

  return (
    <div className="relative w-full aspect-video bg-muted overflow-hidden">
      {showVideo && videoUrl ? (
        <video
          src={videoUrl}
          controls
          autoPlay
          preload="auto"
          className="h-full w-full object-contain bg-black"
          onEnded={() => setShowVideo(false)}
        />
      ) : (
        <>
          {thumbnailUrl && imageStatus !== 'error' ? (
            <>
              {imageStatus === 'loading' && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground/50" />
                </div>
              )}
              <img
                src={thumbnailUrl}
                alt={alt}
                className={`h-full w-full object-cover transition-opacity duration-300 ${
                  imageStatus === 'loaded' ? 'opacity-100' : 'opacity-0'
                }`}
                onLoad={() => setImageStatus('loaded')}
                onError={() => { setImageStatus('error'); onImageError?.() }}
              />
            </>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
              <ImageOff className="h-8 w-8 text-muted-foreground/30" />
              <span className="text-xs text-muted-foreground/50">{t('card.noImage')}</span>
            </div>
          )}

          {/* Always preload video metadata for instant playback */}
          {isVideo && videoUrl && (
            <video src={videoUrl} preload="metadata" className="hidden" />
          )}

          {/* Play button overlay */}
          {isVideo && videoUrl && imageStatus === 'loaded' && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                setShowVideo(true)
              }}
              className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 hover:opacity-100 transition-opacity duration-200"
              aria-label={t('card.playVideo')}
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/90 shadow-lg">
                <Play className="h-5 w-5 text-gray-900 ml-0.5" />
              </div>
            </button>
          )}
        </>
      )}

      {/* Media type badge */}
      <MediaTypeBadge mediaType={mediaType} />
    </div>
  )
}

// DetectionChip — imported from shared UI component
import { DetectionChip } from '@/components/ui/detection-chip'

// Object + audio detection chips
function DetectionChips({ event }: { event: Event }) {
  const { t } = useTranslation('events')
  const detectionMap = useMemo(() => {
    const detections = (event.metadata?.detections as VisualDetection[]) ?? []
    // Max confidence per class across all frames — the best detection of each class,
    // not the arbitrary last-frame value. (#095 follow-up)
    const map: Record<string, number> = {}
    for (const d of detections) map[d.class_name] = Math.max(map[d.class_name] ?? 0, d.confidence)
    return map
  }, [event.metadata])

  const objectClasses = event.detected_objects ?? []
  const audioDetections = event.audio_detections ?? []

  if (!objectClasses.length && !audioDetections.length) return null

  return (
    <div className="mt-2 space-y-1.5">
      {objectClasses.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {objectClasses.map((cls) => (
            <DetectionChip
              key={cls}
              cls={cls}
              confidence={detectionMap[cls]}
              type="object"
            />
          ))}
        </div>
      )}
      {audioDetections.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[9px] uppercase tracking-wide text-muted-foreground/50 font-semibold pr-0.5 select-none">
            {t('card.audio')}
          </span>
          {audioDetections.map((d) => (
            <DetectionChip
              key={d.class_name}
              cls={d.class_name}
              confidence={d.confidence}
              type="audio"
            />
          ))}
        </div>
      )}
    </div>
  )
}

const NO_EVENTS: Event[] = []

// Event card component
const EventCard = memo(function EventCard({
  event,
  index,
  onEventClick,
  selectionMode,
  selected,
  onToggleSelect,
  onDeleteRequest,
  onCameraClick,
  onImageError,
  onAcknowledge,
  acknowledging = false,
  onSeen,
  seeing = false,
}: {
  event: Event
  index: number
  onEventClick: (index: number) => void
  selectionMode: boolean
  selected: boolean
  onToggleSelect: (id: number) => void
  onDeleteRequest: (event: Event) => void
  onCameraClick: (cameraId: number) => void
  /** #336 — see EventMedia; one function per list (useImageRetry), so the memoised card is not drawn again */
  onImageError?: () => void
  /** #392 — acknowledge this open critical event (the feed's one useAcknowledgeCritical; stable) */
  onAcknowledge?: (id: number) => void
  /** #392 — its acknowledgement is on its way */
  acknowledging?: boolean
  /** #406 — press Seen on this open suspicious event (the feed's one useMarkSeen; stable) */
  onSeen?: (id: number) => void
  /** #406 — its Seen is on its way */
  seeing?: boolean
}) {
  const { t } = useTranslation('events')
  const { t: tc } = useTranslation('common')
  const title = useEventTitle()
  // #294 — a critical event: the critical title ("Graffiti spray sound · 64 %"), never "Motion detected"
  const eventTypeDisplay = title(event, event.event_type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()))

  // #392 — a critical card: the red bar and "CRITICAL · Spray sound" label (index.css .crit-card). Open = not
  // acknowledged yet: light red, the state written in the label (and so in the card's accessible name) and an
  // Acknowledge button. Acknowledged: the card's own background, a muted-red bar, who acknowledged it and when.
  const critical = isCriticalEvent(event)
  const criticalOpen = critical && !event.acknowledged_at
  const criticalReason = critical ? criticalReasonText(event, tc as unknown as Translate) : ''
  const labelId = useId()
  const titleId = useId()
  const ackLineId = useId()
  // #406 — a suspicious card: the orange bar and "SUSPICIOUS · <reasons>" label (index.css .sus-card). Open = nobody has
  // pressed Seen: light orange and a Seen button. Seen: the card's own background, a muted bar, who saw it and when.
  const suspicious = isSuspiciousEvent(event)
  const suspiciousOpen = isOpenSuspicious(event)
  const clueReasons = useClueReasons()
  const reasons = suspicious ? clueReasons(event) : []

  const favMutation = useToggleFavorite(event.id) // #122

  const mediaType = event.media_details?.media_type ?? 'image'

  const { original, annotated } = getEventDownloads(event)

  const [showVideo, setShowVideo] = useState(false)

  // #410 — the server says per event whether this person may delete it; selecting is only for those.
  const canDelete = event.can_delete === true

  const handleCardClick = () => {
    if (selectionMode) {
      if (canDelete) onToggleSelect(event.id)
      return
    }
    setShowVideo(false)
    onEventClick(index)
  }

  return (
    <Card
      {...(critical && {
        role: 'article',
        'aria-labelledby': criticalOpen ? `${labelId} ${titleId}` : `${labelId} ${titleId} ${ackLineId}`,
        'data-critical-card': criticalOpen ? 'open' : 'acknowledged',
      })}
      {...(suspicious && {
        role: 'article',
        'aria-labelledby': suspiciousOpen ? `${labelId} ${titleId}` : `${labelId} ${titleId} ${ackLineId}`,
        'data-suspicious-card': suspiciousOpen ? 'open' : 'seen',
      })}
      className={cn(
        'overflow-hidden cursor-pointer transition-all duration-150 flex flex-col relative',
        selectionMode
          ? !canDelete
            ? 'opacity-60 cursor-not-allowed'
            : selected
            ? 'ring-2 ring-primary shadow-md'
            : 'hover:ring-1 hover:ring-primary/40'
          : 'hover:ring-1 hover:ring-primary/30 hover:shadow-sm',
        critical && 'crit-card',
        critical && (criticalOpen ? 'crit-card--open' : 'crit-card--acked'),
        suspicious && 'sus-card',
        suspicious && (suspiciousOpen ? 'sus-card--open' : 'sus-card--seen'),
      )}
      onClick={handleCardClick}
    >
      {/* Selection checkbox overlay */}
      {selectionMode && canDelete && (
        <div className="absolute top-2 right-2 z-10">
          <div
            className={cn(
              'h-5 w-5 rounded border-2 flex items-center justify-center',
              selected
                ? 'bg-primary border-primary'
                : 'bg-background/95 border-muted-foreground/50',
            )}
          >
            {selected && <X className="h-3 w-3 text-primary-foreground" strokeWidth={3} />}
          </div>
        </div>
      )}

      {/* Quick download (annotated) — over the thumbnail, hidden during selection */}
      {!selectionMode && annotated && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); downloadFile(annotated.url, annotated.filename) }}
          aria-label={t('card.download')}
          title={t('card.download')}
          className="absolute top-2 right-2 z-10 rounded-full bg-black/55 p-1.5 text-white opacity-70 transition hover:bg-black/75 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-white/70"
        >
          <Download className="h-4 w-4" />
        </button>
      )}

      <EventMedia
        thumbnailUrl={event.thumbnail_url}
        videoUrl={event.detection_video_url}
        mediaType={mediaType}
        alt={event.event_type}
        showVideo={showVideo}
        setShowVideo={setShowVideo}
        onImageError={onImageError}
      />
      <div className="flex-1 flex flex-col p-3 sm:p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            {critical && (
              <span id={labelId} className="crit-label mb-0.5">
                <b>{tc('critical.label')}</b>
                {criticalReason ? <> · <span>{criticalReason}</span></> : null}
                {criticalOpen && <> · <span>{tc('critical.notAcknowledged')}</span></>}
              </span>
            )}
            {suspicious && (
              <span id={labelId} className="sus-label mb-0.5">
                <b>{tc('suspicious.label')}</b>{reasons.length > 0 ? ` · ${reasons.join(' · ')}` : null}
              </span>
            )}
            <div className="flex items-center gap-2 min-w-0">
              <h3 id={critical || suspicious ? titleId : undefined} className="font-semibold text-sm sm:text-base truncate">{eventTypeDisplay}</h3>
              {event.is_unread && <UnreadBadge />}
              <span
                className={cn(
                  'det-conf-badge whitespace-nowrap shrink-0',
                  event.confidence_score >= 0.8
                    ? 'det-conf-high'
                    : event.confidence_score >= 0.5
                    ? 'det-conf-med'
                    : 'det-conf-low'
                )}
              >
                {Math.round(event.confidence_score * 100)}%
              </span>
            </div>
            {/* #249 — full camera name and group name, always: the row wraps and each name may
                break across lines instead of being cut with an ellipsis on a narrow card. */}
            <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onCameraClick(event.camera) }}
                title={event.camera_name}
                className="inline-flex min-w-0 max-w-full items-start gap-1.5 rounded-sm text-left font-medium text-primary transition-colors hover:text-primary/80 hover:underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                <Camera className="mt-[3px] h-3.5 w-3.5 flex-shrink-0" />
                <span className="min-w-0 break-words">{event.camera_name}</span>
              </button>
              {event.camera_group && (
                <span
                  title={event.camera_group}
                  className="inline-flex min-w-0 max-w-full items-start gap-1 text-xs text-muted-foreground"
                >
                  <FolderOpen className="mt-0.5 h-3 w-3 flex-shrink-0" />
                  <span className="min-w-0 break-words">{event.camera_group}</span>
                </span>
              )}
            </div>
            {critical && !criticalOpen && event.acknowledged_at && (
              <p id={ackLineId} className="mt-1 text-xs sm:text-[13px] font-semibold leading-snug text-emerald-700 dark:text-emerald-400">
                {event.acknowledged_by
                  ? tc('critical.acknowledgedBy', { name: event.acknowledged_by, time: formatCardDateTime(event.acknowledged_at) })
                  : tc('critical.acknowledgedNoName', { time: formatCardDateTime(event.acknowledged_at) })}
              </p>
            )}
            {suspicious && !suspiciousOpen && <SuspiciousSeenLine id={ackLineId} event={event} className="mt-1" />}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* #122 — favourite toggle (hidden during selection mode) */}
            {!selectionMode && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); favMutation.mutate(!event.is_favorite) }}
                aria-label={event.is_favorite ? t('card.unfavorite') : t('card.favorite')}
                title={event.is_favorite ? t('card.unfavorite') : t('card.favorite')}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                <Star className={cn('h-4 w-4', event.is_favorite && 'fill-amber-400 text-amber-400')} />
              </button>
            )}
            {/* Three-dot menu — only visible when not in selection mode */}
            {!selectionMode && (original || annotated || canDelete) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-foreground"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreVertical className="h-4 w-4" />
                    <span className="sr-only">{t('card.eventOptions')}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                  {original && (
                    <DropdownMenuItem
                      onClick={(e) => { e.stopPropagation(); downloadFile(original.url, original.filename) }}
                    >
                      <Download className="mr-2 h-4 w-4" />
                      {t('detail.downloadOriginal')}
                    </DropdownMenuItem>
                  )}
                  {annotated && (
                    <DropdownMenuItem
                      onClick={(e) => { e.stopPropagation(); downloadFile(annotated.url, annotated.filename) }}
                    >
                      <Download className="mr-2 h-4 w-4" />
                      {t('detail.downloadAnnotated')}
                    </DropdownMenuItem>
                  )}
                  {(original || annotated) && canDelete && <DropdownMenuSeparator />}
                  {canDelete && (
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive focus:bg-destructive/10"
                      onClick={(e) => {
                        e.stopPropagation()
                        onDeleteRequest(event)
                      }}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {t('card.deleteEvent')}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        <div className="flex-1">
          <DetectionChips event={event} />
        </div>

        <div className="flex items-center gap-2 mt-auto pt-2">
          <span className="text-[10px] sm:text-xs text-muted-foreground flex items-center gap-1 min-w-0">
            <Calendar className="h-3 w-3 flex-shrink-0" />
            <span className="truncate">{formatCardDateTime(event.occurred_at)}</span>
          </span>
          {/* #392 — the Dashboard row's action, the same endpoint and team effect. A real button of its own: it
              never opens the event (the card's click), like the star and the menu above. Hidden while selecting,
              like the star and the menu: acknowledging is team-wide and cannot be undone, so a click meant to pick
              cards for deletion must never acknowledge (fix round 1, MF1). The written state stays. */}
          {criticalOpen && onAcknowledge && !selectionMode && (
            <button
              type="button"
              className="crit-ack ml-auto"
              onClick={(e) => { e.stopPropagation(); onAcknowledge(event.id) }}
              disabled={acknowledging}
            >
              {tc('critical.acknowledge')}
            </button>
          )}
          {/* #406 — Seen: its own real button, hidden while selecting, for the reason given for Acknowledge above */}
          {suspiciousOpen && onSeen && !selectionMode && (
            <button
              type="button"
              className="sus-seen ml-auto"
              onClick={(e) => { e.stopPropagation(); onSeen(event.id) }}
              disabled={seeing}
            >
              {tc('suspicious.seen')}
            </button>
          )}
        </div>
      </div>
    </Card>
  )
})

// Loading skeleton
function EventCardSkeleton() {
  return (
    <Card className="overflow-hidden">
      <div className="w-full aspect-video shimmer" />
      <div className="p-3 sm:p-4 space-y-3">
        <div className="flex justify-between gap-2">
          <div className="shimmer h-4 w-32 rounded" />
          <div className="shimmer h-5 w-10 rounded-full" />
        </div>
        <div className="shimmer h-3 w-24 rounded" />
        <div className="shimmer h-3 w-full rounded" />
        <div className="shimmer h-3 w-3/4 rounded" />
        <div className="flex justify-between gap-2 pt-2">
          <div className="shimmer h-3 w-20 rounded" />
          <div className="shimmer h-7 w-16 rounded" />
        </div>
      </div>
    </Card>
  )
}

// Camera filter dropdown
function CameraFilter({
  cameras,
  selectedCameraId,
  onSelect,
}: {
  cameras: CameraType[]
  selectedCameraId: number | null
  onSelect: (id: number | null) => void
}) {
  const { t } = useTranslation('events')
  const [search, setSearch] = useState('')
  const selectedCamera = cameras.find((c) => c.id === selectedCameraId)
  const filtered = search.trim()
    ? cameras.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()))
    : cameras

  return (
    <DropdownMenu onOpenChange={(open) => { if (!open) setSearch('') }}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="w-full justify-between">
          <span className="flex items-center gap-2 min-w-0">
            <Camera className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">{selectedCamera ? selectedCamera.name : t('filters.allCameras')}</span>
          </span>
          <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0 ml-2" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[260px] p-0">
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <Search className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
          <input
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            placeholder={t('filters.searchCameras')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
          {search && (
            <button onClick={() => setSearch('')} className="text-muted-foreground hover:text-foreground">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          <DropdownMenuItem onClick={() => onSelect(null)}>
            <span className={cn('text-sm', !selectedCameraId && 'font-medium')}>{t('filters.allCameras')}</span>
          </DropdownMenuItem>
          {filtered.length === 0 && (
            <p className="px-3 py-2 text-xs text-muted-foreground">{t('filters.noCamerasMatch')}</p>
          )}
          {filtered.map((camera) => (
            <DropdownMenuItem key={camera.id} onClick={() => onSelect(camera.id)}>
              <span className={cn('text-sm truncate', selectedCameraId === camera.id && 'font-medium text-primary')}>
                {camera.name}
              </span>
            </DropdownMenuItem>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// #122 — the events feed, reused by both /events and /favourites. `baseFilters`
// is merged into the API filters (Favourites pins `favorite: true`); the empty-state
// strings can be overridden so each surface reads naturally.
export interface EventFeedProps {
  baseFilters?: Partial<import('@/types').EventFilters>
  emptyTitle?: string
  emptyMessage?: string
  showCategoryTabs?: boolean // #123 — Important/Other/All tabs; false hides them (Favourites)
  defaultCategory?: DefaultEventCategory // #248 — the tab a bare URL opens on; EventsPage passes the fleet-wide setting
}

export function EventFeed({
  baseFilters, emptyTitle, emptyMessage, showCategoryTabs = true, defaultCategory = 'all',
}: EventFeedProps = {}) {
  const { t } = useTranslation('events')
  const { t: tc } = useTranslation('common')
  // `searchParams` is still needed for the `?openEvent=` alias and the deep-link close path.
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()
  const locationState = location.state as LocationStateWithFrom | null

  // The URL is the single source of truth for every filter. #124's category tab
  // lives here too, so a back-navigation lands on the tab the user left.
  // /favourites passes showCategoryTabs={false} and gets the schema without it.
  const schema = useMemo(
    () => (showCategoryTabs ? eventsSchema(defaultCategory) : EVENTS_SCHEMA_NO_CATEGORY),
    [showCategoryTabs, defaultCategory],
  )
  const [urlState, setUrlState] = useUrlState(schema)

  const selectedCameraId = urlState.camera
  // #408 — the Suspicious tab is for beta users; without beta a direct ?category=suspicious link reads as All (unknown = off)
  const suspiciousBeta = useSuspiciousBeta()
  const urlCategory = showCategoryTabs
    ? (urlState as StateOf<EventsSchema>).category
    : ('all' as const)
  const category = urlCategory === 'suspicious' && !suspiciousBeta ? ('all' as const) : urlCategory
  // #292 — "Unread only" (the dashboard's Recent Events card links here with ?unread=true).
  // Events page only: /favourites has no such filter.
  const unreadOnly = showCategoryTabs ? (urlState as StateOf<EventsSchema>).unread : false
  // #304 — that card counts no motion-only events, so its link adds ?hide_motion=true. Off by
  // default; shown as a chip that removes it (no permanent control).
  const hideMotion = showCategoryTabs ? (urlState as StateOf<EventsSchema>).hide_motion : false
  const eventTypeFilter = urlState.event_type
  const mediaTypeFilter = urlState.media_type
  const detectedObjectFilter = urlState.detected_object
  const dateRange = useMemo(
    () => ({ start: urlState.start_date, end: urlState.end_date }),
    [urlState.start_date, urlState.end_date],
  )
  // #123 — on the Other tab (no person/animal objects) the event-type and
  // detected-object filters are meaningless, so hide + don't apply them.
  const hideObjectFilters = showCategoryTabs && category === 'other'
  // #392 — the Critical tab: every critical event, open and acknowledged (?critical=all, no category). The other
  // filters stay (critical + a person seen is a useful view); no Select / bulk delete there (owner decision 3).
  const isCriticalTab = showCategoryTabs && category === 'critical'
  // #406 — the Suspicious tab: every suspicious event, seen and unseen (?level=suspicious, no category). Like the
  // Critical tab: the other filters stay, no Select / bulk delete, a failed list is never "nothing to see".
  const isSuspiciousTab = showCategoryTabs && category === 'suspicious'
  const isLevelTab = isCriticalTab || isSuspiciousTab
  // #392 round 3 — phones (under Tailwind's sm, 640 px) get a two-row toolbar that also carries the count and Select;
  // the page header row then only serves the camera pills. Pages without the tabs (Favourites) keep the one row.
  const isPhone = !useMediaQuery('(min-width: 640px)', true)
  const phoneToolbar = isPhone && showCategoryTabs
  // #406 — on a phone the tab strip scrolls: the active tab is brought into view, sideways only (never the page)
  const activeTabRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (phoneToolbar && suspiciousBeta) activeTabRef.current?.scrollIntoView?.({ inline: 'nearest', block: 'nearest' })
  }, [phoneToolbar, suspiciousBeta, category])

  // Lightbox state. The open/closed fact lives in `?event=<id>`; this is the
  // index into composedEvents that the modal renders, derived from that param
  // (and moved by the modal's own prev/next arrows).
  const [selectedEventIndex, setSelectedEventIndex] = useState<number | null>(null)

  // Holds a fetched-by-id event when it's not in the current page-1 list, so we can render the modal on it.
  const [openEventFallback, setOpenEventFallback] = useState<Event | null>(null)
  // #078 addendum: per-event fresh overrides for video events whose detection_video_url
  // wasn't ready when the WS-triggered events-list refetch landed. Keyed by event id.
  const [eventOverrides, setEventOverrides] = useState<Map<number, Event>>(new Map())

  // Fire-and-forget refresh for a video event that may still be processing.
  // If the fetched copy has detection_video_url, stash it as an override so the
  // modal (and any other consumer of composedEvents) renders the playable video.
  const ensureFreshVideo = useCallback((event: Event | undefined) => {
    if (!event) return
    if (event.media_details?.media_type !== 'video') return
    if (event.detection_video_url) return
    eventsApi.getById(event.id)
      .then((fresh) => {
        if (fresh.detection_video_url) {
          setEventOverrides((prev) => {
            const next = new Map(prev)
            next.set(event.id, fresh)
            return next
          })
        }
      })
      .catch(() => {
        // Stay on the thumbnail; user can refresh later if processing failed.
      })
  }, [])

  // `?event=<id>` opens the lightbox. `?openEvent=<id>` is the deprecated alias
  // (#078; only ever emitted in-app by PeekCards — no server-side push or email
  // links carry it) kept accepted for bookmarked or shared URLs: rewrite it to
  // `?event=` and never clear sibling filters. The old handler called
  // clearFilters(), which wiped the user's entire filter set on every click.
  useEffect(() => {
    const legacy = searchParams.get('openEvent')
    if (!legacy) return
    const next = new URLSearchParams(searchParams)
    next.delete('openEvent')
    next.set('event', legacy)
    setSearchParams(next, { replace: true, state: locationState })
  }, [searchParams, setSearchParams, locationState])

  const pendingOpenEventId = urlState.event

  // Selection / bulk delete state. #392 — none on the Critical tab (#406 nor the Suspicious one): switching to it
  // leaves selection mode.
  const [selectionModeState, setSelectionMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const selectionMode = selectionModeState && !isLevelTab
  useEffect(() => {
    if (!isLevelTab) return
    setSelectionMode(false)
    setSelectedIds(new Set())
  }, [isLevelTab])

  // Confirm dialog state
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [eventToDelete, setEventToDelete] = useState<Event | null>(null) // single delete
  const [bulkDeletePending, setBulkDeletePending] = useState(false)      // bulk delete

  // Infinite scroll ref
  const loadMoreRef = useRef<HTMLDivElement>(null)

  // Fetch cameras for filter dropdown
  const { data: cameras = [] } = useQuery({
    queryKey: queryKeys.cameras.all(),
    queryFn: () => camerasApi.getAll(),
    ...queryOptions.cameras.all,
  })

  const { data: detectionClasses = [] } = useQuery({
    queryKey: queryKeys.detectionClasses(),
    queryFn: () => aiModelsApi.getDetectionClasses(),
    ...queryOptions.detectionClasses,
  })

  // Every filter change is a `replace` on the URL (see useUrlState), so it never
  // adds a history entry — back means "go back a page", not "undo my last filter".
  const handleCameraSelect = (cameraId: number | null) => setUrlState({ camera: cameraId })
  const handleCategoryChange = (value: StateOf<EventsSchema>['category']) =>
    setUrlState({ category: value } as Partial<StateOf<typeof schema>>)
  const handleUnreadToggle = () =>
    setUrlState({ unread: !unreadOnly } as Partial<StateOf<typeof schema>>)
  const handleShowMotion = () =>
    setUrlState({ hide_motion: false } as Partial<StateOf<typeof schema>>)
  const handleEventTypeChange = (value: string) => setUrlState({ event_type: value })
  const handleMediaTypeChange = (value: string) => setUrlState({ media_type: value })
  const handleDetectedObjectChange = (value: string) => setUrlState({ detected_object: value })
  const handleDateRangeChange = (range: { start: string; end: string }) =>
    setUrlState({ start_date: range.start, end_date: range.end })

  // Clears only the filters it names — never `event` or `category`, and never a
  // param outside the schema. (The old version called setSearchParams({}), which
  // wiped everything including the tab and the event being opened.)
  const clearFilters = () =>
    setUrlState({
      camera: null,
      event_type: 'all',
      media_type: 'all',
      detected_object: 'all',
      start_date: '',
      end_date: '',
    })

  const hasActiveFilters =
    selectedCameraId !== null ||
    (!hideObjectFilters && eventTypeFilter !== 'all') ||
    mediaTypeFilter !== 'all' ||
    (!hideObjectFilters && detectedObjectFilter !== 'all') ||
    dateRange.start !== '' ||
    dateRange.end !== ''

  // Build API filters
  const filters = {
    ...(selectedCameraId && { camera: selectedCameraId }),
    ...(showCategoryTabs && category !== 'all' && category !== 'critical' && category !== 'suspicious' && { category }),
    ...(isCriticalTab && { critical: 'all' as const }),
    ...(isSuspiciousTab && { level: 'suspicious' as const }),
    ...(unreadOnly && { unread: true }),
    ...(hideMotion && { hide_motion: true }),
    ...(!hideObjectFilters && eventTypeFilter !== 'all' && { event_type: eventTypeFilter as import('@/types').EventType }),
    ...(mediaTypeFilter !== 'all' && { media_type: mediaTypeFilter as 'video' | 'image' }),
    ...(!hideObjectFilters && detectedObjectFilter !== 'all' && { detected_object: detectedObjectFilter }),
    ...(dateRange.start && { start_time: `${dateRange.start}T00:00:00` }),
    ...(dateRange.end && { end_time: `${dateRange.end}T23:59:59` }),
    ...baseFilters, // #122 — Favourites surface pins { favorite: true }
  }

  // #336 — the key of this feed's own list (Events page or Favourites): a card's expired thumbnail refreshes it once
  const listKey = baseFilters?.favorite
    ? queryKeys.events.favourites(filters)
    : queryKeys.events.paginated(filters)
  const { onError: onThumbnailError } = useImageRetry(listKey)

  // Infinite query
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
  } = useInfiniteQuery({
    // #122 — favourites feed gets its own cache key so toggling a star can
    // invalidate it independently of the all-events feed.
    queryKey: listKey,
    queryFn: ({ pageParam = 1 }) => eventsApi.getAll(filters, pageParam),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.next) {
        return allPages.length + 1
      }
      return undefined
    },
    initialPageParam: 1,
    ...queryOptions.events.paginated,
  })

  // Snapshot how deep the user scrolled, and on a back-navigation with a cold
  // cache re-seed pages 1..N before the restore gate releases. The queryKey and
  // fetchPage MUST mirror the useInfiniteQuery above exactly, or the seed lands
  // under a key nothing reads and the restore silently no-ops.
  const pageDepth = data?.pages.length ?? 1

  useRestorableList({
    queryKey: baseFilters?.favorite
      ? queryKeys.events.favourites(filters)
      : queryKeys.events.paginated(filters),
    fetchPage: (page: number) => eventsApi.getAll(filters, page),
    pageDepth,
  })

  const loadedEvents = data?.pages.flatMap((page) => page.results) ?? NO_EVENTS
  // #392 (review R6) — the version guard: on the Critical tab every row must be critical. One that is not means a
  // server that ignored critical=all (an old API after a rollback) and answered every event: show an error, never
  // that list as "critical", nor its count.
  // #406 — the same on the Suspicious tab, for a server that ignored level=suspicious.
  const needsNewerServer = (isCriticalTab && loadedEvents.some((e) => e.is_critical !== true))
    || (isSuspiciousTab && loadedEvents.some((e) => e.level !== 'suspicious'))
  // #392 (fix round 2) — on the Critical tab a failed list (a 400, a 502 around a deploy) is "unknown", never "all
  // clear": the error shows alone, without a "0 events" count or "No critical events". Other tabs: as before.
  const levelListFailed = isLevelTab && isError
  const events = needsNewerServer ? NO_EVENTS : loadedEvents
  const totalCount = data?.pages[0]?.count ?? 0
  const selectedCamera = cameras.find((c) => c.id === selectedCameraId)

  // #078: events the modal renders — overlay any fresh-fetched overrides on the cached list
  // (and prepend openEventFallback for the fetch-by-id path).
  const composedEvents = useMemo(() => {
    const base = openEventFallback ? [openEventFallback, ...events] : events
    if (eventOverrides.size === 0) return base
    return base.map((e) => eventOverrides.get(e.id) ?? e)
  }, [events, openEventFallback, eventOverrides])

  // Hold the latest composedEvents in a ref so click/nav handlers can stay stable.
  // `events` is a fresh array on every render (data.pages.flatMap), so without this the
  // handler deps would churn and break EventCard's React.memo for every visible card.
  const composedEventsRef = useRef(composedEvents)
  composedEventsRef.current = composedEvents

  // The `?event=` id this effect has already acted on. The param stays in the URL
  // for as long as the lightbox is open, so without this the effect would re-fire
  // on every render. Tracking the id (not a boolean) also lets us detect the
  // param DISAPPEARING — that's a Back press, and it must close the modal.
  const handledEventIdRef = useRef<number | null>(null)

  // The id of a `?event=` push whose URL update has not landed yet. main.tsx runs the
  // router with `v7_startTransition`, which makes navigate() a DEFERRED transition while
  // setSelectedEventIndex() below is urgent — so there is a commit where the lightbox is
  // open but the param is still absent. Without this ref the effect below reads that gap
  // as "the param went away" (a Back press) and closes the lightbox a frame after opening
  // it, then reopens it when the URL lands: the "squeeze, vanish, squeeze" flicker.
  const pushedEventIdRef = useRef<number | null>(null)

  // Click handler for EventCard. Open the modal immediately on the cached row; if it's a
  // video without a finalized URL, kick off a background refetch that will swap in the
  // playable video once processing completes (#078 addendum).
  //
  // The PUSH is what makes Back close the lightbox instead of leaving the page.
  // Same pathname, so RestorationProvider leaves the feed's scroll untouched.
  const handleEventClick = useCallback((index: number) => {
    const event = composedEventsRef.current[index]
    if (!event) return
    setSelectedEventIndex(index)
    ensureFreshVideo(event)
    // Mark it handled up front: the effect below runs on the same batched render
    // as this navigate, and would otherwise re-locate the event and fire a second
    // ensureFreshVideo for it.
    handledEventIdRef.current = event.id
    // Claim the push before making it, so the effect below can tell "the URL has not
    // caught up yet" from "the user pressed Back".
    pushedEventIdRef.current = event.id
    // withSearchParam, not `?event=N` — react-router REPLACES the search string
    // rather than merging it, so the bare form would wipe every active filter,
    // re-key the infinite query, and open the modal on the wrong event.
    navigate(
      { search: withSearchParam(searchParams, 'event', String(event.id)) },
      { state: { ...locationState, lightboxPushed: true } satisfies LocationStateWithFrom },
    )
  }, [ensureFreshVideo, navigate, locationState, searchParams])

  // Prev/next navigation inside the modal — same refresh-on-video pattern as a fresh click.
  // Arrows move the index only; the URL keeps naming the event the lightbox was opened on,
  // so one Back press closes the modal no matter how far the user arrowed.
  const handleModalNavigate = useCallback((idx: number) => {
    setSelectedEventIndex(idx)
    ensureFreshVideo(composedEventsRef.current[idx])
  }, [ensureFreshVideo])

  // Modal close. If we pushed the entry, pop it — stripping the param instead would
  // leave that entry dead in the stack and the next Back press would appear to do
  // nothing. A deep link has nothing behind it, so there we strip the param, since
  // navigate(-1) would eject the user out of the app. See lib/navigation.ts.
  const handleModalClose = useCallback(() => {
    setSelectedEventIndex(null)
    setOpenEventFallback(null)
    if (locationState?.lightboxPushed) {
      navigate(-1)
      return
    }
    navigate(
      { search: withSearchParam(searchParams, 'event', null) },
      { replace: true, state: locationState },
    )
  }, [navigate, locationState, searchParams])

  // Drive the lightbox from `?event=<id>` (#078, now URL-backed).
  //
  // Filters are NOT cleared to find the event any more — a notification click used
  // to wipe the user's whole filter set. If the event isn't in the loaded list
  // (filtered out, or simply on a later page), the fetch-by-id fallback renders it.
  useEffect(() => {
    const prev = handledEventIdRef.current

    if (pendingOpenEventId === null) {
      // A push of ours is still in flight: the param is not gone, it has not arrived.
      // Closing here would tear the lightbox down one commit after it opened.
      if (pushedEventIdRef.current !== null) return

      handledEventIdRef.current = null
      // The param went away while the modal was open: a Back press. Close it.
      if (prev !== null) {
        setSelectedEventIndex(null)
        setOpenEventFallback(null)
      }
      return
    }

    // The URL caught up with our push. Release the claim, so the NEXT disappearance of
    // the param is a real Back press and does close the lightbox.
    if (pushedEventIdRef.current === pendingOpenEventId) {
      pushedEventIdRef.current = null
    }

    if (prev === pendingOpenEventId) return  // already acted on this id
    if (isLoading) return                    // wait for the query; do NOT consume the id

    handledEventIdRef.current = pendingOpenEventId

    // Index into composedEvents — that's what the modal renders, and it may carry a
    // prepended fallback event, which would shift a raw `events` index by one.
    const idx = composedEventsRef.current.findIndex((e) => e.id === pendingOpenEventId)
    if (idx >= 0) {
      setSelectedEventIndex(idx)
      ensureFreshVideo(composedEventsRef.current[idx])  // swap to playable video if it just finished processing
      return
    }

    // Not in the loaded list — fall back to fetch-by-id.
    eventsApi.getById(pendingOpenEventId)
      .then((event) => {
        setOpenEventFallback(event)
        setSelectedEventIndex(0)
        // Fallback fetch is already fresh; no extra refetch needed.
      })
      .catch(() => {
        toast.error(t('peek.eventNoLongerAvailable', { ns: 'notifications' }))
      })
  }, [pendingOpenEventId, events, isLoading, t, ensureFreshVideo])

  // Intersection Observer for infinite scroll
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  )

  useEffect(() => {
    const element = loadMoreRef.current
    if (!element) return
    const observer = new IntersectionObserver(handleObserver, {
      // Observe the actual scroll container (<main>), not the viewport, so the
      // "load more" trigger fires at the right position on mobile too.
      root: element.closest('main'),
      threshold: 0.1,
      rootMargin: '100px',
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [handleObserver])

  const activeFilterCount = [
    selectedCameraId !== null,
    !hideObjectFilters && eventTypeFilter !== 'all',
    mediaTypeFilter !== 'all',
    !hideObjectFilters && detectedObjectFilter !== 'all',
    dateRange.start !== '' || dateRange.end !== '',
  ].filter(Boolean).length

  // #392 — Acknowledge on an open critical card: the Dashboard row's hook (optimistic in every cached list, the
  // same endpoint, the team-wide effect and the WebSocket message). One per feed; the cards get a stable callback.
  const acknowledgeMutation = useAcknowledgeCritical()
  const acknowledgeMutate = acknowledgeMutation.mutate
  const handleAcknowledge = useCallback((id: number) => acknowledgeMutate(id), [acknowledgeMutate])
  const acknowledgingId = acknowledgeMutation.isPending && typeof acknowledgeMutation.variables === 'number'
    ? acknowledgeMutation.variables
    : null
  // #406 — Seen on an open suspicious card, the same way (the Dashboard row's hook)
  const seenMutation = useMarkSeen()
  const seenMutate = seenMutation.mutate
  const handleSeen = useCallback((id: number) => seenMutate(id), [seenMutate])
  const seeingId = seenMutation.isPending && typeof seenMutation.variables === 'number' ? seenMutation.variables : null

  // ── Delete mutations ──────────────────────────────────────────────────────

  const invalidateEvents = () => {
    queryClient.invalidateQueries({ queryKey: ['events'] })
  }

  // Single delete
  const deleteMutation = useMutation({
    mutationFn: (id: number) => eventsApi.delete(id),
    onSuccess: () => {
      toast.success(t('messages.deleted'))
      invalidateEvents()
      setConfirmOpen(false)
      setEventToDelete(null)
    },
    onError: () => {
      toast.error(t('messages.deleteFailed'))
    },
  })

  // Bulk delete — sequential deletions
  const bulkDeleteMutation = useMutation({
    // One request instead of N sequential deletes (#097). Server does the full
    // cleanup (S3 + linked media + raw file) and returns a partial result.
    mutationFn: (ids: number[]) => eventsApi.bulkDelete(ids),
    onSuccess: (result) => {
      const deleted = result.deleted?.length ?? 0
      const failed = result.failed?.length ?? 0
      if (failed > 0) {
        toast.warning(t('messages.bulkDeletedPartial', { deleted, failed }))
      } else {
        toast.success(t('messages.bulkDeleted', { count: deleted }))
      }
      setSelectedIds(new Set())
      setSelectionMode(false)
      setConfirmOpen(false)
      setBulkDeletePending(false)
      invalidateEvents()
    },
    onError: () => {
      toast.error(t('messages.bulkDeleteFailed'))
      invalidateEvents()
    },
  })

  // ── Selection helpers ─────────────────────────────────────────────────────

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const exitSelectionMode = () => {
    setSelectionMode(false)
    setSelectedIds(new Set())
  }

  // "Select all" = all currently-loaded events, capped at the server's per-
  // request limit (#097). A whole-history wipe isn't an id-list operation.
  const BULK_DELETE_LIMIT = 500
  // #410 — only the events the server lets this person delete can be selected; with none, Select is not offered.
  const deletableEvents = events.filter((e) => e.can_delete === true)
  const canSelectAny = deletableEvents.length > 0
  const selectAll = () => {
    const ids = deletableEvents.slice(0, BULK_DELETE_LIMIT).map((e) => e.id)
    setSelectedIds(new Set(ids))
    if (deletableEvents.length > BULK_DELETE_LIMIT) {
      toast.info(t('selection.selectAllCapped', { count: BULK_DELETE_LIMIT }))
    }
  }
  const allSelected =
    deletableEvents.length > 0 && selectedIds.size >= Math.min(deletableEvents.length, BULK_DELETE_LIMIT)

  // ── Confirm dialog helpers ────────────────────────────────────────────────

  const requestDeleteSingle = (event: Event) => {
    setEventToDelete(event)
    setBulkDeletePending(false)
    setConfirmOpen(true)
  }

  const requestDeleteSelected = () => {
    setBulkDeletePending(true)
    setEventToDelete(null)
    setConfirmOpen(true)
  }

  const handleConfirmDelete = () => {
    if (bulkDeletePending) {
      bulkDeleteMutation.mutate(Array.from(selectedIds))
    } else if (eventToDelete) {
      deleteMutation.mutate(eventToDelete.id)
    }
  }

  const isDeleting = deleteMutation.isPending || bulkDeleteMutation.isPending

  const confirmTitle = bulkDeletePending
    ? t('deleteConfirm.titleBulk', { count: selectedIds.size })
    : t('deleteConfirm.title')

  const confirmDescription = bulkDeletePending
    ? t('deleteConfirm.descriptionBulk', { count: selectedIds.size })
    : t('deleteConfirm.descriptionSingle')

  const countText = selectedCamera
    ? t('headerCameraCount', { name: selectedCamera.name, count: totalCount })
    : t('headerCount', { count: totalCount })

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Page Header. #392 round 3 — with the phone toolbar the count and Select live in the toolbar below, and
          this row is only rendered for the camera pills (a camera in the URL; from the first paint, so the toolbar
          does not move down when the cameras load). */}
      {(!phoneToolbar || selectedCameraId) && (
      <div className="flex items-start justify-between gap-4" data-events-header="">
        <div className="space-y-2 min-w-0">
          {/* #125/#129 — slim header (nav shows the page title). Reserve the count's space
              with a skeleton while loading so it doesn't pop in (layout shift); swap for the
              live count / camera name after fetch. */}
          {phoneToolbar ? null : isLoading ? (
            <div className="h-7 sm:h-8 w-28 rounded-md shimmer" aria-hidden="true" />
          ) : needsNewerServer || levelListFailed ? null : (
            <h2 className="text-xl sm:text-2xl font-semibold tracking-tight truncate">{countText}</h2>
          )}
          {/* Telemetry pills — only when a single camera is selected (#038) */}
          {selectedCamera && <CameraTelemetryPills camera={selectedCamera} />}
        </div>

        {/* #129 — Select toggle is always mounted (disabled while loading / when empty) so
            the header keeps a stable shape from first paint. #392 — the Critical tab has no Select, so the
            slot reserves the button's height (min-h-9 = Button sm) and the sticky toolbar does not jump. */}
        {!phoneToolbar && (
        <div className="flex items-center gap-2 flex-shrink-0 mt-1 min-h-9">
          {selectionMode && (
            <Button
              variant="outline"
              size="sm"
              onClick={allSelected ? () => setSelectedIds(new Set()) : selectAll}
            >
              {allSelected ? t('selection.deselectAll') : t('selection.selectAll')}
            </Button>
          )}
          {!isLevelTab && (selectionMode || canSelectAny) && (
            <Button
              variant={selectionMode ? 'secondary' : 'outline'}
              size="sm"
              disabled={!selectionMode && (isLoading || events.length === 0)}
              onClick={selectionMode ? exitSelectionMode : () => setSelectionMode(true)}
            >
              <CheckSquare className="h-4 w-4 mr-2" />
              {selectionMode ? tc('actions.cancel') : t('selection.select')}
            </Button>
          )}
        </div>
        )}
      </div>
      )}

      {/* Bulk action toolbar — slides in when items are selected */}
      {selectionMode && selectedIds.size > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-2.5">
          <span className="text-sm font-medium text-destructive">
            {t('selection.selected', { count: selectedIds.size })}
          </span>
          <Button
            variant="destructive"
            size="sm"
            onClick={requestDeleteSelected}
            disabled={isDeleting}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            {t('selection.deleteSelected')}
          </Button>
        </div>
      )}

      {/* #123 — sticky control toolbar: ONE thin row (tabs + a Filters popover) pinned under the
          app header while the long event list scrolls. Full-bleed (negative margins cancel
          MainLayout's padding) with an opaque background so cards scroll cleanly underneath. The
          five filters collapse into one popover so the bar stays a single row and scales to any
          screen; the trigger badges the active-filter count. */}
      <div
        className={cn(
          'sticky top-0 z-20 -mx-3 px-3 sm:-mx-4 sm:px-4 md:-mx-6 md:px-6 lg:-mx-8 lg:px-8 bg-background border-b border-border/60',
          phoneToolbar ? 'py-1.5' : 'py-2',
        )}
        data-toolbar={phoneToolbar ? 'phone' : 'wide'}
      >
        {/* #392 round 3 — phones: two pinned rows, the tabs then the filters with the count and Select at the right;
            from sm up the one row as before (tabs right, filters left). #406 — six labels do not fit in equal cells:
            on phones the tab row is a strip that scrolls sideways, 44 px tall, the active tab kept in view. From sm to
            md they take less padding to fit beside the filters, and the row wraps when active filters still leave too
            little room, the tabs dropping to their own line whole: nothing scrolls there, a cut tab would load cut.
            #408 — that is for beta users. Everyone else keeps the five tabs: equal cells on a phone, one row from sm up. */}
        <div className={phoneToolbar ? 'flex flex-col gap-1.5' : cn('flex flex-row-reverse items-center justify-between gap-3', suspiciousBeta && 'flex-wrap')}>
          {showCategoryTabs ? (
            <div
              className={cn(
                'flex rounded-lg border border-border bg-muted/50 p-0.5',
                phoneToolbar
                  ? cn('w-full overflow-x-auto', suspiciousBeta && 'snap-x [scrollbar-width:none] [&::-webkit-scrollbar]:hidden')
                  : 'min-w-0 max-w-full overflow-x-auto',
              )}
              role="tablist"
            >
              {(suspiciousBeta
                ? (['animal', 'human', 'other', 'all', 'suspicious', 'critical'] as const)
                : (['animal', 'human', 'other', 'all', 'critical'] as const)
              ).map((c) => (
                <button
                  key={c}
                  ref={category === c ? activeTabRef : undefined}
                  type="button"
                  role="tab"
                  aria-selected={category === c}
                  onClick={() => handleCategoryChange(c)}
                  className={cn(
                    'whitespace-nowrap text-sm font-medium rounded-md transition-colors',
                    suspiciousBeta
                      ? (phoneToolbar ? cn('shrink-0 min-w-[5.5rem] h-11 px-1.5 text-center', category === c && 'snap-center') : 'shrink-0 sm:px-2 md:px-3 py-1.5')
                      : cn('py-1.5', phoneToolbar ? 'flex-1 px-1.5 text-center' : 'shrink-0 px-3'),
                    category === c
                      ? 'bg-amber-400 text-amber-950 shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {t(`tabs.${c}`)}
                </button>
              ))}
            </div>
          ) : (
            <span />
          )}

          <div className={cn('flex items-center gap-2', phoneToolbar ? 'min-w-0' : 'flex-shrink-0')}>
          {/* #392 round 3 — phones: the filter controls scroll sideways in their own group (the page clips overflow)
              and give way while selecting (Select all + Cancel would not fit beside them); `contents` from sm up. */}
          {(!phoneToolbar || !selectionMode) && (
          <div
            className={phoneToolbar
              ? 'flex min-w-0 flex-initial items-center gap-2 overflow-x-auto px-1 -mx-1 py-1 -my-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
              : 'contents'}
          >
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="flex-shrink-0">
                <SlidersHorizontal className="h-4 w-4 mr-2" />
                {t('filters.title')}
                {activeFilterCount > 0 && (
                  <span className="ml-2 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-primary">
                    {activeFilterCount}
                  </span>
                )}
                <ChevronDown className="ml-1.5 h-4 w-4 opacity-60" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 space-y-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">{t('filters.dateRange')}</label>
                <DateRangePicker
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  placeholder={t('filters.dateRange')}
                  className="w-full"
                  maxDate={new Date()}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">{t('filters.camera')}</label>
                <CameraFilter
                  cameras={cameras.filter((c) => !c.is_locked)}
                  selectedCameraId={selectedCameraId}
                  onSelect={handleCameraSelect}
                />
              </div>

              {!hideObjectFilters && (
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground">{t('filters.eventType')}</label>
                  <Select value={eventTypeFilter} onValueChange={handleEventTypeChange}>
                    <SelectTrigger className="w-full bg-background">
                      <SelectValue placeholder={t('filters.eventType')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('filters.allEvents')}</SelectItem>
                      <SelectItem value="person_detected">{t('filters.personDetected')}</SelectItem>
                      <SelectItem value="vehicle_detected">{t('filters.vehicleDetected')}</SelectItem>
                      <SelectItem value="animal_detected">{t('filters.animalDetected')}</SelectItem>
                      <SelectItem value="motion_detected">{t('filters.motionDetected')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">{t('filters.mediaType')}</label>
                <Select value={mediaTypeFilter} onValueChange={handleMediaTypeChange}>
                  <SelectTrigger className="w-full bg-background">
                    <SelectValue placeholder={t('filters.mediaType')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('filters.allMedia')}</SelectItem>
                    <SelectItem value="video">
                      <span className="flex items-center gap-2"><Video className="h-3.5 w-3.5" />{t('filters.videos')}</span>
                    </SelectItem>
                    <SelectItem value="image">
                      <span className="flex items-center gap-2"><Image className="h-3.5 w-3.5" />{t('filters.photos')}</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {!hideObjectFilters && (
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground">{t('filters.detectedObject')}</label>
                  <Select value={detectedObjectFilter} onValueChange={handleDetectedObjectChange}>
                    <SelectTrigger className="w-full bg-background">
                      <SelectValue placeholder={t('filters.detectedObject')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('filters.allObjects')}</SelectItem>
                      {detectionClasses.map((cls) => (
                        <SelectItem key={cls.id} value={cls.class_name}>
                          <span className="capitalize">{tc(`detectionClasses.${cls.class_name}`, { defaultValue: cls.class_name.replace(/_/g, ' ') })}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="w-full justify-center">
                  <X className="h-4 w-4 mr-1" />
                  {t('filters.clear')}
                </Button>
              )}
            </PopoverContent>
          </Popover>
          {showCategoryTabs && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-pressed={unreadOnly}
              onClick={handleUnreadToggle}
              className={cn(
                'flex-shrink-0',
                unreadOnly && 'border-amber-400 bg-amber-400 text-amber-950 hover:bg-amber-400/90 hover:text-amber-950',
              )}
            >
              {t('filters.unreadOnly')}
            </Button>
          )}
          {showCategoryTabs && hideMotion && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleShowMotion}
              title={t('filters.showMotion')}
              className="flex-shrink-0"
            >
              {t('filters.motionHidden')}
              <X className="h-3.5 w-3.5 ml-1" aria-hidden="true" />
            </Button>
          )}
          </div>
          )}
          {/* the right-hand group grows into what the filter group leaves (the count truncates) and is never
              narrower than the Select icon */}
          {phoneToolbar && (
            <span className="flex flex-1 min-w-[2.125rem] items-center justify-end gap-2">
              {selectionMode ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={allSelected ? () => setSelectedIds(new Set()) : selectAll}
                  >
                    {allSelected ? t('selection.deselectAll') : t('selection.selectAll')}
                  </Button>
                  <Button variant="secondary" size="sm" onClick={exitSelectionMode}>
                    {tc('actions.cancel')}
                  </Button>
                </>
              ) : (
                <>
                  {/* the count (the h2 of wider screens); hidden under 360 px where it would only truncate, and
                      gives way to the "Motion hidden" chip */}
                  {hideMotion ? null : isLoading ? (
                    <span className="h-3 w-14 rounded shimmer" aria-hidden="true" />
                  ) : needsNewerServer || levelListFailed ? null : (
                    <span className="hidden min-[360px]:inline min-w-0 truncate text-xs text-muted-foreground">
                      {t('headerCount', { count: totalCount })}
                    </span>
                  )}
                  {!isLevelTab && canSelectAny && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="shrink-0 px-2"
                      aria-label={t('selection.select')}
                      title={t('selection.select')}
                      disabled={isLoading || events.length === 0}
                      onClick={() => setSelectionMode(true)}
                    >
                      <CheckSquare className="h-4 w-4" />
                    </Button>
                  )}
                </>
              )}
            </span>
          )}
          </div>
        </div>
      </div>

      {/* Error State */}
      {isError && (
        <Card className="border-destructive">
          <CardContent className="flex h-32 items-center justify-center text-destructive">
            {t('messages.errorLoading', { message: (error as Error)?.message || 'Unknown error' })}
          </CardContent>
        </Card>
      )}

      {/* #392 — the version guard (see needsNewerServer) */}
      {needsNewerServer && (
        <Card className="border-destructive" role="alert">
          <CardContent className="flex h-32 items-center justify-center text-center text-destructive">
            {t(isSuspiciousTab ? 'messages.suspiciousNeedsNewerServer' : 'messages.criticalNeedsNewerServer')}
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <EventCardSkeleton key={i} />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !needsNewerServer && !levelListFailed && events.length === 0 && (
        <Card>
          <CardContent className="flex h-64 flex-col items-center justify-center text-center p-4">
            <Activity className="mb-4 h-10 w-10 sm:h-12 sm:w-12 text-muted-foreground" />
            {/* #392 — the Critical tab says why it can be empty: only cameras with graffiti detection raise them */}
            <h3 className="text-base sm:text-lg font-semibold">
              {isCriticalTab
                ? t(hasActiveFilters || unreadOnly ? 'empty.criticalTitleFiltered' : 'empty.criticalTitle')
                : isSuspiciousTab
                ? t(hasActiveFilters || unreadOnly ? 'empty.suspiciousTitleFiltered' : 'empty.suspiciousTitle')
                : (emptyTitle ?? t('empty.title'))}
            </h3>
            <p className="text-xs sm:text-sm text-muted-foreground mt-2 max-w-md">
              {isCriticalTab
                ? t('empty.criticalMessage')
                : isSuspiciousTab
                ? t('empty.suspiciousMessage')
                : hasActiveFilters
                ? t('empty.filterMessage')
                : (emptyMessage ?? t('empty.message'))}
            </p>
            {hasActiveFilters && (
              <Button variant="outline" className="mt-4" onClick={clearFilters}>
                {t('filters.clearFilters')}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Events Grid */}
      {!isLoading && events.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {composedEvents.map((event, index) => (
              <EventCard
                key={event.id}
                event={event}
                index={index}
                onEventClick={handleEventClick}
                selectionMode={selectionMode}
                selected={selectedIds.has(event.id)}
                onToggleSelect={toggleSelect}
                onDeleteRequest={requestDeleteSingle}
                onCameraClick={handleCameraSelect}
                onImageError={onThumbnailError}
                onAcknowledge={handleAcknowledge}
                acknowledging={acknowledgingId === event.id}
                onSeen={handleSeen}
                seeing={seeingId === event.id}
              />
            ))}
          </div>

          {/* Load More Trigger */}
          <div ref={loadMoreRef} className="flex justify-center py-4">
            {isFetchingNextPage && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>{t('messages.loading')}</span>
              </div>
            )}
            {!hasNextPage && events.length > 0 && (
              <p className="text-sm text-muted-foreground">
                {t('messages.showingAll', { count: events.length })}
              </p>
            )}
          </div>
        </>
      )}

      {/* Event detail lightbox */}
      <EventDetailModal
        events={composedEvents}
        selectedIndex={selectedEventIndex}
        targetId={pendingOpenEventId}
        onClose={handleModalClose}
        onNavigate={handleModalNavigate}
      />

      {/* Confirm delete dialog */}
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!isDeleting) {
            setConfirmOpen(open)
            if (!open) {
              setEventToDelete(null)
              setBulkDeletePending(false)
            }
          }
        }}
        title={confirmTitle}
        description={confirmDescription}
        confirmLabel={isDeleting ? t('deleteConfirm.deleting') : bulkDeletePending ? t('deleteConfirm.confirmBulk', { count: selectedIds.size }) : t('card.deleteEvent')}
        loading={isDeleting}
        onConfirm={handleConfirmDelete}
      />
    </div>
  )
}
